import React, { createContext, useContext, useState, ReactNode } from 'react';

export type ChatDataSource = {
  sourceId: number;
  sourceType: string;
  timestamp?: string;
};

export interface ChatData {
  message: string;
  elementContext: string;
  sourceType: string;
  sourceId: number;
  sources?: ChatDataSource[];
}

interface ChatContextType {
  isChatOpen: boolean;
  setIsChatOpen: (open: boolean) => void;
  chatData: ChatData | null;
  setChatData: (data: ChatData | null) => void;
  openChatWithData: (data: ChatData) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChatContext = () => {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }

  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatData, setChatData] = useState<ChatData | null>(null);

  const openChatWithData = (data: ChatData) => {
    setChatData(data);
    setIsChatOpen(true);
  };

  return (
    <ChatContext.Provider
      value={{
        isChatOpen,
        setIsChatOpen,
        chatData,
        setChatData,
        openChatWithData,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
